import parsePhoneNumber, { AsYouType, parseIncompletePhoneNumber, type CountryCode } from 'libphonenumber-js'
import { countryPhoneService } from '@widgets/shared/services/countryPhoneService'
import { TmFakeCountryCode, type TmRealCountryCode, type TmCountryCode } from '@/services/types'

export default class PhoneService {
  protected readonly tmFakePhoneNumberPrefix = '+999'

  public format(phone: string, countryId: CountryCode | TmCountryCode = 'US'): string {
    if (!this.canBeParsed(phone)) {
      return phone
    }

    return this.formatPhone(this.getPhoneWithLeadingPlus(phone), countryId)
  }

  public preparePhoneWithCountryCodeForUpload(countryCode: TmCountryCode, phone: string) {
    const clearedPhone = this.clearPhoneAndSavePlus(phone)
    return countryPhoneService.makeInternationalPhone(countryCode, clearedPhone)
  }

  public clearPhoneAndSavePlus(phone: string) {
    return phone.replace(/[^\d+]/g, '')
  }

  public isLocalPhoneFormat(phone: string) {
    return !phone.startsWith('+') && !phone.startsWith('00')
  }

  public getHrefForCalling(phone: string) {
    return `tel:${this.getPhoneWithLeadingPlus(this.clearPhoneAndSavePlus(phone))}`
  }

  public getHrefForSms(phone: string) {
    return `sms:${this.getPhoneWithLeadingPlus(this.clearPhoneAndSavePlus(phone))}`
  }

  public getHrefForWhatsapp(phone: string) {
    return `https://wa.me/${this.getPhoneWithLeadingPlus(this.clearPhoneAndSavePlus(phone))}`
  }

  public isValidPhoneNumber(phone: string, countryCode: TmCountryCode): boolean {
    const phoneNumber = this.getPhoneWithLeadingPlus(this.clearPhoneAndSavePlus(phone))

    if (this.isFakePhoneNumber(phoneNumber) || countryCode === TmFakeCountryCode) {
      return phoneNumber.length > 4 && phoneNumber.length < 17
    }

    return this.isValidRealPhoneNumber(phone, countryCode)
  }

  public cleanPhoneNumber(rawPhoneNumber) {
    return rawPhoneNumber.replace(/\D/g, '')
  }

  public getExceptionalPhoneCountryCode(phoneNumber: string): CountryCode | null {
    const preparedPhone = this.cleanPhoneNumber(phoneNumber)
    const phoneLength = preparedPhone.length

    if (
      preparedPhone.startsWith('49') &&
      ['13', '55', '99', '00', '50', '33'].includes(preparedPhone.slice(5, 7)) &&
      phoneLength === 14
    ) {
      return 'DE' // Germany
    }

    if (preparedPhone.startsWith('315') && phoneLength === 10) {
      return 'NL' // Netherlands
    }

    if ((preparedPhone.startsWith('46719') || preparedPhone.startsWith('46710')) && phoneLength === 15) {
      return 'SE' // Sweden
    }

    if (preparedPhone.startsWith('62') && phoneLength === 11) {
      return 'ID' // Indonesia
    }

    if (preparedPhone.startsWith('475800') && phoneLength === 14) {
      return 'NO' // Norway
    }

    if (preparedPhone.startsWith('4759') && phoneLength === 10) {
      return 'NO' // Norway
    }

    if (preparedPhone.startsWith('230') && phoneLength >= 10 && phoneLength <= 11) {
      return 'MU' // Mauritius
    }

    if (preparedPhone.startsWith('337') && phoneLength >= 14 && phoneLength <= 16) {
      return 'FR' // France
    }

    if (preparedPhone.startsWith('34590') && phoneLength === 15) {
      return 'ES' // Spain
    }

    if (phoneLength === 11) {
      const usCodes = ['1645', '1728', '1945']
      const phoneKey = preparedPhone.slice(0, 4)
      if (usCodes.includes(phoneKey)) return 'US' // USA
    }

    return null
  }

  public isValidRealPhoneNumber(phone: string, countryCode?: CountryCode): boolean {
    const phoneNumber = this.getPhoneWithLeadingPlus(phone)

    if (this.getExceptionalPhoneCountryCode(phoneNumber)) {
      return true
    }

    try {
      if (!this.canBeParsed(phoneNumber)) {
        return false
      }

      const phoneParsed = parsePhoneNumber(countryCode ? phone : phoneNumber, countryCode)
      if (countryCode && phoneParsed && phoneParsed.country !== countryCode) {
        return false
      }
      if (phoneParsed) {
        return phoneParsed.isValid()
      }

      return false
    } catch {
      return false
    }
  }

  protected canBeParsed(phone: string): boolean {
    return !!parseIncompletePhoneNumber(phone).length
  }

  protected formatPhone(phone: string, countryId: TmCountryCode): string {
    if (this.isFakeCountry(countryId)) {
      return '99999999999'
    }

    return new AsYouType(countryId as TmRealCountryCode).input(phone)
  }

  protected isFakeCountry(countryCode: TmCountryCode): boolean {
    return countryCode === TmFakeCountryCode
  }

  protected getPhoneWithLeadingPlus(phone: string): string {
    if (phone.startsWith('00')) {
      return this.addPlusToPhoneNumber(phone.slice(2))
    }
    return this.addPlusToPhoneNumber(phone)
  }

  protected addPlusToPhoneNumber(phone: number | string): string {
    return String(phone).replace(/^\+?(.)/, '+$1')
  }

  protected removePlusFromPhoneNumber(phone: string): string {
    return String(phone).replace('+', '')
  }

  protected isFakePhoneNumber(phone: string): boolean {
    return phone.startsWith(this.tmFakePhoneNumberPrefix)
  }
}

export const phoneService = new PhoneService()
